import { ComponentRef, FC, ReactNode, RefObject } from "react";
import {
  KeyboardAwareScrollView,
  KeyboardAwareScrollViewProps,
} from "react-native-keyboard-controller";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { VnBox } from "@/components/VnBox";
import { VnScrollView } from "@/components/VnScrollView";
import { usePlatformOS } from "@/hooks/usePlatformOS";
import { cn } from "@/utils/tailwind";

export type ScreenLayoutPropsType = {
  children: ReactNode;
  variant?: "FORM" | "SCROLL" | "LIST";
  className?: string;
  backgroundClassName?: string;
  showScrollBar?: boolean;
  hasHeader?: boolean;
  hasTabBar?: boolean;
  keyboardAwareScrollViewProps?: KeyboardAwareScrollViewProps;
  hasTopPadding?: boolean;
  hasHorizontalPadding?: boolean;
  scrollViewRef?: RefObject<ComponentRef<typeof VnScrollView> | null>;
};

export const ScreenLayout: FC<ScreenLayoutPropsType> = ({
  children,
  variant = "SCROLL",
  className,
  backgroundClassName = "bg-background",
  showScrollBar = false,
  hasHeader = true,
  keyboardAwareScrollViewProps,
  hasHorizontalPadding = true,
  hasTopPadding = true,
  scrollViewRef,
}) => {
  const insets = useSafeAreaInsets();
  const { isWeb } = usePlatformOS();

  if (isWeb) {
    return (
      <VnBox className={cn("flex-1", backgroundClassName)}>
        <VnScrollView
          ref={scrollViewRef}
          className="flex-1"
          contentContainerClassName="flex-grow">
          <VnBox
            className={cn(
              "container mx-auto flex-1",
              {
                "px-4": hasHorizontalPadding,
              },
              className,
              backgroundClassName,
            )}>
            {children}
          </VnBox>
        </VnScrollView>
      </VnBox>
    );
  }

  switch (variant) {
    case "FORM":
      return (
        <VnBox
          className={cn(
            "flex-1",
            {
              "pt-safe": hasTopPadding,
            },
            backgroundClassName,
          )}>
          <KeyboardAwareScrollView
            className={cn(
              {
                "px-4": hasHorizontalPadding,
              },
              backgroundClassName,
            )}
            keyboardDismissMode="on-drag"
            contentContainerClassName="flex-grow"
            bottomOffset={insets.bottom}
            showsVerticalScrollIndicator={showScrollBar}
            {...keyboardAwareScrollViewProps}>
            <>
              {children}
              {/* Spacer to avoid content being too close to the bottom */}
              <VnBox className="h-8 w-full" />
            </>
          </KeyboardAwareScrollView>
        </VnBox>
      );

    case "LIST":
      return (
        <VnBox
          className={cn(
            "flex-1",
            {
              "pt-safe": !hasHeader,
            },
            backgroundClassName,
            className,
          )}>
          {children}
        </VnBox>
      );

    default:
    case "SCROLL":
      return (
        <VnBox
          className={cn(
            "flex-1",
            {
              "pt-safe": !hasHeader,
            },
            backgroundClassName,
            className,
          )}>
          <VnScrollView
            ref={scrollViewRef}
            showsVerticalScrollIndicator={showScrollBar}>
            <VnBox
              className={cn(
                "flex-1",
                {
                  "px-4": hasHorizontalPadding,
                },
                backgroundClassName,
              )}>
              {children}
            </VnBox>
          </VnScrollView>
        </VnBox>
      );
  }
};
